import dayjs from "dayjs";
import { useMemo, useRef } from "react";

import type { DateRangeValue } from "../../../context/urlParams";
import type {
  TradingReportParams,
  TradingReportStates,
} from "../providers/TradingReportProvider";

import { decodeDateRange, encodeSearch } from "../../../context/urlParams";
import { decodeIdList } from "../../utils/decodeIdList";

export type DecodedTradingReportParams = {
  rebateAccountIDs: string[];
  rebateProgramIDs: string[];
  range: DateRangeValue;
};

/**
 * Every search key this hook owns and will write back to the URL on filter
 * change. Exported so the caller's `writeSearch` can strip these from the
 * previous search before merging the new `encoded` values -- preserving
 * everything else the hook has no opinion on (e.g. the portal's `lng`, the
 * backoffice route's `tab`, unrelated params a parent page added).
 */
export const TRADING_REPORT_SYNCED_SEARCH_KEYS = [
  "rebateAccountIDs",
  "rebateProgramIDs",
  "startDate",
  "endDate",
] as const satisfies readonly (
  | keyof DecodedTradingReportParams
  | "startDate"
  | "endDate"
)[];

export type UseTradingReportUrlParamsInput = {
  /** The current route search, already read by the caller (each app owns its
   * own `useSearch` call, keeping this hook framework-agnostic). */
  search: Record<string, unknown>;
  /** Range to decode to when the URL carries no valid `startDate`/`endDate`
   * (e.g. the report's own "last 30 days" default). */
  defaultDateRange: DateRangeValue;
  /**
   * Writes the encoded managed-key search to the URL. The hook only emits
   * `TRADING_REPORT_SYNCED_SEARCH_KEYS`; it is the caller's job to merge
   * them into the existing search without dropping unrelated keys (the
   * portal's `lng`, the backoffice route's `tab`) or the hash -- otherwise
   * every filter change silently loses them, which looks like Apply doing
   * nothing when it drops the `#trading-report` hash on the Clients page.
   */
  writeSearch: (encoded: Record<string, string>) => void;
};

export type UseTradingReportUrlParamsResult = {
  hasUrlParams: boolean;
  decoded: DecodedTradingReportParams | undefined;
  initialState: Partial<TradingReportStates> | undefined;
  providerKey: string;
  handleParamsChange: (params: TradingReportParams) => void;
};

/**
 * Route-agnostic URL-param sync for the Trading report, shared by portal and
 * backoffice — mirrors `usePerformanceReportUrlParams`'s contract: decodes the
 * current search into a provider `initialState` once on mount (so a Clients
 * or Total-earnings hover-link from the Shared Rebate Programs listing opens
 * pre-filtered), and returns a `handleParamsChange` that keeps the URL in
 * sync with every later filter change (date range, rebate account/program
 * selection), so the report stays bookmarkable and shareable.
 *
 * Unlike Performance Report's facets, `rebateAccountIDs`/`rebateProgramIDs`
 * are opaque backend ids, not a closed enum the UI must validate against —
 * the backend already treats an unknown id as "no matching rows" (and the
 * selected chip falls back to showing the raw id), so this intentionally
 * skips Performance Report's async whitelist-loading gate
 * (`isOptionsLoading`/`optionsError`/`fullyInvalidFacets`) and decodes
 * immediately.
 */
export const useTradingReportUrlParams = ({
  search,
  defaultDateRange,
  writeSearch,
}: UseTradingReportUrlParamsInput): UseTradingReportUrlParamsResult => {
  const hasUrlParams =
    Boolean(search.rebateAccountIDs) ||
    Boolean(search.rebateProgramIDs) ||
    Boolean(search.startDate) ||
    Boolean(search.endDate);

  const decoded = useMemo((): DecodedTradingReportParams | undefined => {
    if (!hasUrlParams) return undefined;
    const { range } = decodeDateRange(
      search as { startDate?: string; endDate?: string },
      defaultDateRange,
    );
    return {
      rebateAccountIDs: decodeIdList(search.rebateAccountIDs),
      rebateProgramIDs: decodeIdList(search.rebateProgramIDs),
      range,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `search`/`defaultDateRange` are read by identity on mount only; a fresh object on every render must not retrigger the decode.
  }, [
    hasUrlParams,
    search.rebateAccountIDs,
    search.rebateProgramIDs,
    search.startDate,
    search.endDate,
  ]);

  const initialState: Partial<TradingReportStates> | undefined = decoded
    ? {
        dateRange: { from: decoded.range.from, to: decoded.range.to },
        selected: {
          rebateAccountIDs: decoded.rebateAccountIDs.length
            ? decoded.rebateAccountIDs
            : null,
          rebateProgramIDs: decoded.rebateProgramIDs.length
            ? decoded.rebateProgramIDs
            : null,
          clientTypes: null,
          subIBIDs: null,
        },
      }
    : undefined;

  // The provider reads `initialState` once, lazily, on first mount only (see
  // `createReportContext`) -- a stable key avoids remounting (and discarding
  // in-progress edits) on every later URL write from `handleParamsChange`.
  const providerKey = useRef(
    hasUrlParams ? "url-params" : "no-url-params",
  ).current;

  const lastWrittenSearchRef = useRef<string | null>(null);

  const handleParamsChange = (params: TradingReportParams) => {
    const encoded = encodeSearch(
      {
        rebateAccountIDs: params.selected.rebateAccountIDs,
        rebateProgramIDs: params.selected.rebateProgramIDs,
      },
      {
        from: dayjs(params.startDate).toDate(),
        to: dayjs(params.endDate).toDate(),
      },
    );
    const nextKey = JSON.stringify(encoded);
    if (lastWrittenSearchRef.current === nextKey) return;
    lastWrittenSearchRef.current = nextKey;
    writeSearch(encoded);
  };

  return {
    hasUrlParams,
    decoded,
    initialState,
    providerKey,
    handleParamsChange,
  };
};
